import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { PRIORITIES } from './plannerConstants';

const PHASES = ['Foundation Setup', 'Automation', 'Operational Control', 'Historical Data', 'Testing', 'Pre-Cutover Plan', 'Cutover', 'Custom'];

export function AddTaskDialog({ open, onOpenChange, accountId, members, onCreated }) {
  const [form, setForm] = useState({ title: '', task_detail: '', phase: 'Custom', priority: 'Medium', assigned_user_id: 'unassigned', planned_start_date: '', planned_end_date: '', expected_volume: '', volume_unit: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_user_id: form.assigned_user_id === 'unassigned' ? null : form.assigned_user_id };
      await api.post('/onboarding-planner/tasks', payload, { params: { account_id: accountId } });
      toast.success('Custom task added');
      onOpenChange(false);
      setForm({ title: '', task_detail: '', phase: 'Custom', priority: 'Medium', assigned_user_id: 'unassigned', planned_start_date: '', planned_end_date: '', expected_volume: '', volume_unit: '' });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-task-dialog">
        <DialogHeader>
          <DialogTitle>Add Custom Task</DialogTitle>
          <DialogDescription>Add an organisation-specific task to your onboarding plan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="add-task-title" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Detail</Label>
            <Textarea rows={2} value={form.task_detail} onChange={(e) => setForm({ ...form, task_detail: e.target.value })} data-testid="add-task-detail" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Phase</Label>
              <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                <SelectTrigger data-testid="add-task-phase"><SelectValue /></SelectTrigger>
                <SelectContent>{PHASES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="add-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Assign To</Label>
              <Select value={form.assigned_user_id} onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}>
                <SelectTrigger data-testid="add-task-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Planned Start</Label>
              <Input type="date" value={form.planned_start_date} onChange={(e) => setForm({ ...form, planned_start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Planned End</Label>
              <Input type="date" value={form.planned_end_date} onChange={(e) => setForm({ ...form, planned_end_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Expected Volume (optional)</Label>
              <Input type="number" min="0" value={form.expected_volume} onChange={(e) => setForm({ ...form, expected_volume: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Volume Unit</Label>
              <Input placeholder="e.g. runs" value={form.volume_unit} onChange={(e) => setForm({ ...form, volume_unit: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-task-submit">{saving ? 'Adding…' : 'Add Task'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddTaskDialog;
